import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ViewName =
  | "overview"
  | "library"
  | "backlog"
  | "manuscript"
  | "review"
  | "matrix"
  | "code_data"
  | "knowledge"
  | "search"
  | "trash"
  | "brief"
  | "templates"
  | "import"
  | "files"
  | "correspondence"
  | "learning"
  | "settings";

/**
 * Full-page screens that are not tabs and hide the notebook/project browser: Settings, and the
 * Backlog, Files and Weekly review across every notebook and project.
 */
export type PageName = "settings" | "backlog" | "files" | "review" | "archive";

export interface Tab {
  key: string;
  kind: "note" | "paper" | "view";
  id?: string;
  view?: ViewName;
  workspaceId?: string;
  title: string;
  page?: number;
  params?: Record<string, string>;
}

export interface TabSet {
  tabs: Tab[];
  activeKey: string | null;
}

export type RightPanel = null | "sources" | "comments" | "assistant";
export type EditorMode = "edit" | "preview" | "split";

interface UiState {
  userId: string | null;
  theme: "light" | "dark" | "system";
  railCollapsed: boolean;
  browserCollapsed: boolean;
  focusMode: boolean;
  rightPanel: RightPanel;
  editorMode: EditorMode;
  /** Notes use the whole width of the page, or a narrower column that is easier to read. */
  noteWidth: "full" | "readable";
  /** The outline of headings beside a note. */
  noteOutline: boolean;
  workspaceByUser: Record<string, string>;
  /** Open tabs per user and per notebook/project: each one keeps its own tabs. */
  tabsByUser: Record<string, Record<string, TabSet>>;
  page: { name: PageName; params?: Record<string, string> } | null;
  mobileNavOpen: boolean;
  setUser(id: string | null): void;
  setTheme(t: UiState["theme"]): void;
  toggleRail(): void;
  toggleBrowser(): void;
  setFocusMode(v: boolean): void;
  setRightPanel(p: RightPanel): void;
  setEditorMode(m: EditorMode): void;
  setNoteWidth(w: UiState["noteWidth"]): void;
  setNoteOutline(v: boolean): void;
  setWorkspace(id: string): void;
  openTab(tab: Tab, opts?: { activate?: boolean }): void;
  closeTab(key: string): void;
  activate(key: string): void;
  renameTab(key: string, title: string): void;
  updateTab(key: string, patch: Partial<Tab>): void;
  /** Forget the tabs of a notebook or project that no longer exists. */
  dropWorkspaceTabs(workspaceId: string): void;
  openPage(name: PageName, params?: Record<string, string>): void;
  closePage(): void;
  setMobileNav(v: boolean): void;
}

const empty: TabSet = { tabs: [], activeKey: null };

type Buckets = Record<string, TabSet>;

function withoutKey(set: TabSet, key: string): TabSet {
  const idx = set.tabs.findIndex((t) => t.key === key);
  if (idx < 0) return set;
  const tabs = set.tabs.filter((t) => t.key !== key);
  let activeKey = set.activeKey;
  if (activeKey === key) activeKey = tabs[Math.min(idx, tabs.length - 1)]?.key ?? null;
  return { tabs, activeKey };
}

export const useUi = create<UiState>()(
  persist(
    (set, get) => {
      /** Apply a change to the signed-in user's tab buckets (and optionally switch notebook/project). */
      const mutate = (fn: (buckets: Buckets, current: string | undefined) => { buckets: Buckets; workspace?: string } | null, extra: Partial<UiState> = {}) => {
        const u = get().userId;
        if (!u) return;
        set((s) => {
          const res = fn({ ...(s.tabsByUser[u] ?? {}) }, s.workspaceByUser[u]);
          if (!res) return extra;
          return {
            ...extra,
            tabsByUser: { ...s.tabsByUser, [u]: res.buckets },
            ...(res.workspace ? { workspaceByUser: { ...s.workspaceByUser, [u]: res.workspace } } : {}),
          };
        });
      };
      return {
        userId: null,
        theme: "system",
        railCollapsed: false,
        browserCollapsed: false,
        focusMode: false,
        rightPanel: null,
        editorMode: "edit",
        noteWidth: "full",
        noteOutline: true,
        workspaceByUser: {},
        tabsByUser: {},
        page: null,
        mobileNavOpen: false,
        setUser: (id) => set({ userId: id }),
        setTheme: (theme) => set({ theme }),
        toggleRail: () => set((s) => ({ railCollapsed: !s.railCollapsed })),
        toggleBrowser: () => set((s) => ({ browserCollapsed: !s.browserCollapsed })),
        setFocusMode: (focusMode) => set({ focusMode }),
        setRightPanel: (rightPanel) => set({ rightPanel }),
        setEditorMode: (editorMode) => set({ editorMode }),
        setNoteWidth: (noteWidth) => set({ noteWidth }),
        setNoteOutline: (noteOutline) => set({ noteOutline }),
        setWorkspace: (id) => {
          const u = get().userId;
          if (!u) return;
          set((s) => ({ workspaceByUser: { ...s.workspaceByUser, [u]: id }, page: null }));
        },
        openTab: (tab, opts = {}) =>
          mutate(
            (buckets, current) => {
              // A tab lives in its notebook/project; opening one there also switches to it.
              const ws = tab.workspaceId ?? current;
              if (!ws) return null;
              const cur = buckets[ws] ?? empty;
              const existing = cur.tabs.find((t) => t.key === tab.key);
              const next = { ...tab, workspaceId: ws };
              const tabs = existing ? cur.tabs.map((t) => (t.key === tab.key ? { ...t, ...next } : t)) : [...cur.tabs, next];
              buckets[ws] = { tabs, activeKey: opts.activate === false ? cur.activeKey : tab.key };
              return { buckets, workspace: opts.activate === false ? undefined : ws };
            },
            { mobileNavOpen: false, ...(opts.activate === false ? {} : { page: null }) },
          ),
        closeTab: (key) =>
          mutate((buckets) => {
            for (const ws of Object.keys(buckets)) buckets[ws] = withoutKey(buckets[ws], key);
            return { buckets };
          }),
        activate: (key) =>
          mutate(
            (buckets, current) => {
              if (!current) return null;
              buckets[current] = { ...(buckets[current] ?? empty), activeKey: key };
              return { buckets };
            },
            { mobileNavOpen: false, page: null },
          ),
        renameTab: (key, title) => get().updateTab(key, { title }),
        updateTab: (key, patch) =>
          mutate((buckets, current) => {
            let changed = false;
            let follow: string | undefined;
            for (const ws of Object.keys(buckets)) {
              const tab = buckets[ws].tabs.find((t) => t.key === key);
              if (!tab) continue;
              changed = true;
              const updated = { ...tab, ...patch };
              if (patch.workspaceId && patch.workspaceId !== ws) {
                // The document belongs to another notebook/project (e.g. after a move): its tab goes there.
                const wasActive = ws === current && buckets[ws].activeKey === key;
                buckets[ws] = withoutKey(buckets[ws], key);
                const dest = buckets[patch.workspaceId] ?? empty;
                buckets[patch.workspaceId] = {
                  tabs: [...dest.tabs.filter((t) => t.key !== key), updated],
                  activeKey: wasActive || !dest.activeKey ? key : dest.activeKey,
                };
                if (wasActive) follow = patch.workspaceId;
              } else {
                buckets[ws] = { ...buckets[ws], tabs: buckets[ws].tabs.map((t) => (t.key === key ? updated : t)) };
              }
            }
            return changed ? { buckets, workspace: follow } : null;
          }),
        dropWorkspaceTabs: (workspaceId) =>
          mutate((buckets) => {
            if (!buckets[workspaceId]) return null;
            delete buckets[workspaceId];
            return { buckets };
          }),
        openPage: (name, params) => set({ page: { name, params }, mobileNavOpen: false, focusMode: false }),
        closePage: () => set({ page: null }),
        setMobileNav: (mobileNavOpen) => set({ mobileNavOpen }),
      };
    },
    {
      name: "brainlog-claude-ui",
      version: 2,
      partialize: (s) => ({
        theme: s.theme,
        railCollapsed: s.railCollapsed,
        browserCollapsed: s.browserCollapsed,
        editorMode: s.editorMode,
        noteWidth: s.noteWidth,
        noteOutline: s.noteOutline,
        workspaceByUser: s.workspaceByUser,
        tabsByUser: s.tabsByUser,
      }),
      migrate: (persisted, version) => {
        const p = (persisted ?? {}) as Record<string, any>;
        if (version < 2 && p.tabsByUser) {
          // Version 1 kept one list of tabs per user: split it per notebook/project, dropping the
          // Settings and My tasks tabs (they are full pages now).
          const next: Record<string, Buckets> = {};
          for (const [user, v] of Object.entries(p.tabsByUser as Record<string, { tabs?: Tab[]; activeKey?: string | null }>)) {
            const buckets: Buckets = {};
            // Tabs opened without a notebook/project go to the one that was selected (notes move to
            // their own when they load).
            const fallback = (p.workspaceByUser as Record<string, string> | undefined)?.[user];
            for (const t of v?.tabs ?? []) {
              if (t.kind === "view" && ["settings", "tasks"].includes(String(t.view))) continue;
              const ws = t.workspaceId ?? fallback;
              if (!ws) continue;
              const b = (buckets[ws] ??= { tabs: [], activeKey: null });
              b.tabs.push({ ...t, workspaceId: ws });
            }
            for (const b of Object.values(buckets)) b.activeKey = b.tabs.some((t) => t.key === v?.activeKey) ? v!.activeKey! : (b.tabs.at(-1)?.key ?? null);
            next[user] = buckets;
          }
          p.tabsByUser = next;
        }
        return p as UiState;
      },
    },
  ),
);

/** Tabs of the selected notebook or project. */
export function useTabs(): TabSet {
  const userId = useUi((s) => s.userId);
  return useUi((s) => {
    if (!userId) return empty;
    const ws = s.workspaceByUser[userId];
    return (ws && s.tabsByUser[userId]?.[ws]) || empty;
  });
}

/** Every open tab of the signed-in user, in all notebooks and projects. */
export function allTabs(): Tab[] {
  const s = useUi.getState();
  const u = s.userId;
  if (!u) return [];
  return Object.values(s.tabsByUser[u] ?? {}).flatMap((b) => b.tabs);
}

export function useAllTabs(): Tab[] {
  const userId = useUi((s) => s.userId);
  const buckets = useUi((s) => (userId ? s.tabsByUser[userId] : undefined));
  return Object.values(buckets ?? {}).flatMap((b) => b.tabs);
}

export function useCurrentWorkspaceId() {
  const userId = useUi((s) => s.userId);
  return useUi((s) => (userId ? s.workspaceByUser[userId] : undefined));
}

/** Helpers to open common tabs. */
export const openNote = (id: string, title = "Note", workspaceId?: string) =>
  useUi.getState().openTab({ key: `note:${id}`, kind: "note", id, title, workspaceId });
export const openPaper = (id: string, title = "Paper", page?: number, workspaceId?: string) =>
  useUi.getState().openTab({ key: `paper:${id}`, kind: "paper", id, title, page, workspaceId });
export const openView = (view: ViewName, workspaceId: string | undefined, title: string, params?: Record<string, string>, id?: string) =>
  useUi.getState().openTab({
    key: `view:${view}:${id ?? workspaceId ?? "all"}`,
    kind: "view",
    view,
    workspaceId,
    title,
    params,
    id,
  });
export const openSettings = (params?: Record<string, string>) => useUi.getState().openPage("settings", params);
/** Settings → Administration (administrators only). */
export const openAdmin = () => openSettings({ tab: "admin" });
/** Backlog, Files or Weekly review across every notebook and project (optionally pre-filtered). */
export const openEverywhere = (name: "backlog" | "files" | "review", params?: Record<string, string>) => useUi.getState().openPage(name, params);
/** Archived notebooks, projects and notes (from the rail). */
export const openArchive = () => useUi.getState().openPage("archive");
